import { measureLatency } from '../../../gctl-measure';
import {
  ResourceLeadCaptureFormFields,
  ResourceLeadCaptureForm,
} from '../../../models/components/schemas/resource-lead-capture-form/ResourceLeadCaptureForm';
import {
  Select,
  NetworkError,
  deepCamelCase,
  deepSnakeCase,
  searchQueryString,
  intoSnakeCase,
} from '../../../fetchers/_helpers';

/**
 * This file was automatically generated by @gctl/openapi-fetchers
 * Do not modify this file, any changes will be destroyed when
 * the command is rerun.
 */

export type GetLeadCaptureResourceIdFormFields = readonly ResourceLeadCaptureFormFields[];

/** Options for GET /lead_capture/{resource_type}/{resource_id}/form */
export type GetLeadCaptureResourceIdFormOptions<Paths extends readonly string[]> = Omit<
  RequestInit,
  'method' | 'body'
> & {
  baseUrl?: string;

  /** Available fields on the response type, application/vnd.vimeo.resource_lead_capture_forms+json */
  select: Paths;
  where: {
    /**
     * The type of resource.
     *
     * Option descriptions:
     *  * `albums` - The resource is a showcase.
     *  * `live_events` - The resource is an event.
     *  * `videos` - The resource is a video.
     *
     */
    resourceType: `albums` | `live_events` | `videos`;
    /**
     * The ID of the resource.
     */
    resourceId: string;
  };

  /** Possible query parameters for /lead_capture/{resource_type}/{resource_id}/form */
  query?: {
    /**
     * The ID of the registered user. Use this ID to resolve dynamic tags.
     */
    leadId?: string;

    /**
     * The ID of the parent entity lead.
     */
    parentLeadUuid?: string;

    /**
     * The password of the resource when the privacy is set to `password`.
     */
    password?: string;

    /**
     * The pixel dimensions of the image in `{width}x{height}` format. For more information, see our [Using Common Formats and Parameters](https://developer.vimeo.com/api/common-formats#using-the-sizes-parameter) guide.
     */
    sizes?: string;
  };
};

/** Result type for GET /lead_capture/{resource_type}/{resource_id}/form */
export type GetLeadCaptureResourceIdFormResult<Paths extends readonly string[]> = Select<
  ResourceLeadCaptureForm,
  Paths
>;

/**
 * Get information about a lead capture form for a resource
 * This method returns information about the lead capture form for the specified resource.
 */
export async function getLeadCaptureResourceIdForm<
  Paths extends GetLeadCaptureResourceIdFormFields,
>({
  baseUrl,
  select,
  where: { resourceType, resourceId },
  query,
  ...options
}: GetLeadCaptureResourceIdFormOptions<Paths>): Promise<GetLeadCaptureResourceIdFormResult<Paths>> {
  return measureLatency('getLeadCaptureResourceIdForm', 'GET', async () => {
    let res = await fetch(
      `${baseUrl}/lead_capture/${resourceType}/${resourceId}/form?${searchQueryString(
        query,
      )}&fields=${select.map(intoSnakeCase).join(',')}`,
      {
        ...options,
        method: 'GET',
      },
    );

    if (!res.ok) {
      throw new NetworkError('A network error occurred', res.status, res);
    }

    if (res.status === 204) {
      return null as unknown as GetLeadCaptureResourceIdFormResult<Paths>;
    }

    if (!res.headers.get('content-type')?.match(/^application\/(.+)?json$/)) {
      throw new Error('Expected JSON response');
    }

    let json = await res.json();
    return deepCamelCase(json);
  });
}

export type PostLeadCaptureResourceIdFormFields = readonly ResourceLeadCaptureFormFields[];

/** Options for POST /lead_capture/{resource_type}/{resource_id}/form */
export type PostLeadCaptureResourceIdFormOptions<Paths extends readonly string[]> = Omit<
  RequestInit,
  'method' | 'body'
> & {
  baseUrl?: string;

  /** Available fields on the response type, application/vnd.vimeo.resource_lead_capture_forms+json */
  select: Paths;
  where: {
    /**
     * The type of resource.
     *
     * Option descriptions:
     *  * `albums` - The resource is a showcase.
     *  * `live_events` - The resource is an event.
     *  * `videos` - The resource is a video.
     *
     */
    resourceType: `albums` | `live_events` | `videos`;
    /**
     * The ID of the resource.
     */
    resourceId: string;
  };

  /** Data serialized to body for POST /lead_capture/{resource_type}/{resource_id}/form */
  variables?: {
    /**
     * Whether the form is enabled.
     */

    enabled?: boolean;
  };
};

/** Result type for POST /lead_capture/{resource_type}/{resource_id}/form */
export type PostLeadCaptureResourceIdFormResult<Paths extends readonly string[]> = Select<
  ResourceLeadCaptureForm,
  Paths
>;

/**
 * Create a lead capture form for a resource
 * This method creates a lead capture form for the resource.
 */
export async function postLeadCaptureResourceIdForm<
  Paths extends PostLeadCaptureResourceIdFormFields,
>({
  baseUrl,
  select,
  variables,
  where: { resourceType, resourceId },
  ...options
}: PostLeadCaptureResourceIdFormOptions<Paths>): Promise<
  PostLeadCaptureResourceIdFormResult<Paths>
> {
  return measureLatency('postLeadCaptureResourceIdForm', 'POST', async () => {
    let res = await fetch(
      `${baseUrl}/lead_capture/${resourceType}/${resourceId}/form?fields=${select
        .map(intoSnakeCase)
        .join(',')}`,
      {
        ...options,
        method: 'POST',
        body: JSON.stringify(deepSnakeCase(variables)),
      },
    );

    if (!res.ok) {
      throw new NetworkError('A network error occurred', res.status, res);
    }

    if (res.status === 204) {
      return null as unknown as PostLeadCaptureResourceIdFormResult<Paths>;
    }

    if (!res.headers.get('content-type')?.match(/^application\/(.+)?json$/)) {
      throw new Error('Expected JSON response');
    }

    let json = await res.json();
    return deepCamelCase(json);
  });
}

export type PatchLeadCaptureResourceIdFormFields = readonly ResourceLeadCaptureFormFields[];

/** Options for PATCH /lead_capture/{resource_type}/{resource_id}/form */
export type PatchLeadCaptureResourceIdFormOptions<Paths extends readonly string[]> = Omit<
  RequestInit,
  'method' | 'body'
> & {
  baseUrl?: string;

  /** Available fields on the response type, application/vnd.vimeo.video_lead_capture_forms+json */
  select: Paths;
  where: {
    /**
     * The type of resource.
     *
     * Option descriptions:
     *  * `albums` - The resource is a showcase.
     *  * `live_events` - The resource is an event.
     *  * `videos` - The resource is a video.
     *
     */
    resourceType: `albums` | `live_events` | `videos`;
    /**
     * The ID of the resource.
     */
    resourceId: string;
  };

  /** Data serialized to body for PATCH /lead_capture/{resource_type}/{resource_id}/form */
  variables?: {
    /**
     * The alignment of the form.
     *
     * Option descriptions:
     *  * `center` - The form structure is center-aligned.
     *  * `left` - The form structure is left-aligned.
     *  * `right` - The form structure is right-aligned.
     *
     * @example center
     */

    alignment?: `center` | `left` | `right`;

    /**
     * Information about the form's graphical background.
     */

    background?: {
      /**
       * The hexadecimal color code of the color overlay.
       * @example #000000
       */

      hexCode?: string;

      /**
       * Whether a graphical background is enabled for the form.
       * @example true
       */

      isActive?: boolean;

      /**
       * The opacity percentage value of the color overlay.
       * @example 40
       */

      opacity?: number;

      /**
       * The style of the form's background.
       *
       * Option descriptions:
       *  * `crop` - The image gets cropped centrally to appear on the form's background.
       *  * `fill` - The image stretches to fill the form's background.
       *  * `fit` - The image appears in its original size on the form's background.
       *  * `tile` - The image appears multiple times to fill the form's background.
       *
       */

      style?: `crop` | `fill` | `fit` | `tile`;

      /**
       * The type of the form's background.
       *
       * Option descriptions:
       *  * `gif` - The form's background is a GIF.
       *  * `image` - The form's background is a static image.
       *
       */

      type?: `gif` | `image`;

      /**
       * The URI of the form's background image.
       * @example /users/141698208/lead_capture_form/pictures/a60767cbc0ffad8bf6ecb7a39c024163
       */

      uri?: null | string;

      /**
       * Whether to use the thumbnail image as the form's background.
       * @example true
       */

      useThumbnail?: boolean;
    };

    /**
     * The HTML body of the confirmation page description.
     * @example <p style="text-align: center"><span style="font-size: 36px"><strong>Register to watch the resource</span></p>
     */

    confirmationPageDescription?: string;

    /**
     * The HTML body of the confirmation page title.
     * @example <p style="text-align: center"><span style="font-size: 36px"><strong>Check your email for confirmation</span></p>
     */

    confirmationPageTitle?: null | string;

    /**
     * The custom fields to show in the form.
     */

    customFields?: {
      /**
       * The email provider fields associated with the custom field in the form.
       */

      connectedFields?: {
        /**
         * The ID of the email provider field connected with the custom field.
         */

        emailServiceProviderFieldId?: string;

        /**
         * A third-party email service provider associated with the custom field.
         *
         * Option descriptions:
         *  * `1` - The provider is Mailchimp.
         *  * `2` - The provider is Campaign Monitor.
         *  * `3` - The provider is Constant Contact.
         *  * `4` - The provider is Infusionsoft.
         *  * `5` - The provider is HubSpot.
         *  * `6` - The provider is Constant Contact V3.
         *  * `7` - The provider is Marketo.
         *  * `8` - The provider is SalesCloud.
         *
         */

        emailServiceProviderType?: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8;

        /**
         * The ID of the custom field in the form associated with the connected field.
         */

        leadCaptureFormFieldId?: number;
      }[];

      /**
       * Whether the custom field's **is_required** property is editable but **name** isn't. If the value is `true`, the field can't be deleted.
       */

      isLocked?: boolean;

      /**
       * Whether the custom field is required.
       */

      isRequired?: boolean;

      /**
       * Whether the custom field's **is_required** and **name** properties are both uneditable. If the value is `true`, the field can't be deleted.
       */

      isStatic?: boolean;

      /**
       * The metadata object that is associated with the custom field in the form.
       */

      metadata?: {
        /**
         * The color of the checkbox label custom field.
         */

        color?: string;

        /**
         * The description of the checkbox label custom field.
         */

        description?: string;

        /**
         * An array of options that are associated with the custom field.
         */

        options?: {
          /**
           * The label for the custom field option.
           */

          optionLabel?: string;

          /**
           * The position of the custom field option in the dropdown relative to the others.
           */

          optionPosition?: number;
        }[];
      };

      /**
       * The name of a custom field in the form.
       */

      name?: string;

      /**
       * The position of the custom field in the form relative to the others.
       */

      position?: number;

      /**
       * The type of the custom field.
       *
       * Option descriptions:
       *  * `checkbox` - The custom field is used for checkbox item selection.
       *  * `dropdown` - The custom field is used for dropdown item selection.
       *  * `text` - The custom field is used for text input.
       *
       */

      type?: `checkbox` | `dropdown` | `text`;
    }[];

    /**
     * The mailing lists on the designated third-party email service provider that submissions should get synced to.
     */

    emailLists?: {
      /**
       * The ID of the mailing list in the third-party email service provider's system.
       */

      listId?: string;

      /**
       * The third-party email service provider.
       *
       * Option descriptions:
       *  * `1` - The provider is Mailchimp.
       *  * `2` - The provider is Campaign Monitor.
       *  * `3` - The provider is Constant Contact.
       *  * `4` - The provider is Infusionsoft.
       *  * `5` - The provider is HubSpot.
       *  * `6` - The provider is Constant Contact V3.
       *  * `7` - The provider is Marketo.
       *  * `8` - The provider is SalesCloud.
       *
       */

      providerId?: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8;
    }[];

    /**
     * Whether the form is enabled.
     */

    enabled?: boolean;

    /**
     * The fields that are hidden on the registration form. These fields appear only on the management page.
     */

    hiddenFields?: {
      /**
       * The email provider fields associated with the hidden field in the form.
       */

      connectedFields?: {
        /**
         * The ID of the email provider field connected with the hidden field.
         */

        emailServiceProviderFieldId?: string;

        /**
         * A third-party email service provider associated with the hidden field.
         *
         * Option descriptions:
         *  * `1` - The provider is Mailchimp.
         *  * `2` - The provider is Campaign Monitor.
         *  * `3` - The provider is Constant Contact.
         *  * `4` - The provider is Infusionsoft.
         *  * `5` - The provider is HubSpot.
         *  * `6` - The provider is Constant Contact V3.
         *  * `7` - The provider is Marketo.
         *  * `8` - The provider is SalesCloud.
         *
         */

        emailServiceProviderType?: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8;

        /**
         * The ID of the hidden field in the form associated with the connected field.
         */

        leadCaptureFormFieldId?: number;
      }[];

      /**
       * Whether the hidden field's **is_required** property is editable but **name** isn't. If the value is `true`, the field can't be deleted.
       */

      isLocked?: boolean;

      /**
       * Whether the hidden field is required.
       */

      isRequired?: boolean;

      /**
       * Whether the hidden field's **is_required** and **name** properties are both uneditable. If the value is `true`, the field can't be deleted.
       */

      isStatic?: boolean;

      /**
       * The metadata object that is associated with the hidden field in the form.
       */

      metadata?: {
        /**
         * An array of options that are associated with the hidden field.
         */

        options?: {
          /**
           * The label for the hidden field option.
           */

          optionLabel?: string;

          /**
           * The position of the hidden field option in the dropdown relative to the others.
           */

          optionPosition?: number;
        }[];
      };

      /**
       * The name of a hidden field in the form.
       */

      name?: string;

      /**
       * The position of the hidden field in the form relative to the others.
       */

      position?: number;

      /**
       * The type of hidden field.
       *
       * Option descriptions:
       *  * `dropdown` - The hidden field is used for dropdown item selection.
       *  * `text` - The hidden field is used for text input.
       *
       */

      type?: `dropdown` | `text`;
    }[];

    /**
     * The HTML body of the join page title.
     * @example <p style="text-align: center"><span style="font-size: 36px"><strong>Register to watch the resource</span></p>
     */

    joinPageTitle?: string;

    /**
     * The layout of the lead capture form.
     *
     * Option descriptions:
     *  * `multi_step` - The form contains one field per page.
     *  * `single_step` - The form contains all fields in a single page.
     *
     * @example multi_step
     */

    layout?: `multi_step` | `single_step`;

    /**
     * The HTML body of the login page text.
     * @example <p style="text-align: center"><span style="font-size: 36px"><strong>Check your email for confirmation</span></p>
     */

    loginPageTitle?: null | string;

    /**
     * Information about the form's logo.
     */

    logo?: {
      /**
       * When **logo.is_link_active** is `true`, the destination of the logo link.
       * @example https:://vimeo.com/user141698208/1000
       */

      customLink?: null | string;

      /**
       * Whether a logo is enabled for the form.
       * @example true
       */

      isActive?: boolean;

      /**
       * Whether the logo has an associated link.
       * @example true
       */

      isLinkActive?: boolean;

      /**
       * The URI of the form's logo.
       * @example /users/1/team_logos/46085
       */

      uri?: null | string;
    };

    /**
     * The connections of the team members who have connected their email service providers.
     */

    memberConnections?: {
      /**
       * The list of email service providers connected by the user.
       */

      providers?: {
        /**
         * The ID of the email service provider.
         *
         * Option descriptions:
         *  * `1` - The provider is Mailchimp.
         *  * `2` - The provider is Campaign Monitor.
         *  * `3` - The provider is Constant Contact.
         *  * `4` - The provider is Infusionsoft.
         *  * `5` - The provider is HubSpot.
         *  * `6` - The provider is Constant Contact V3.
         *  * `7` - The provider is Marketo.
         *  * `8` - The provider is SalesCloud.
         *
         */

        providerId?: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8;
      }[];

      /**
       * The ID of the user who connected the email service provider.
       * @example 152184
       */

      userId?: number;
    }[];

    /**
     * Information about the styles in the noneditable text fields of the form.
     */

    nonEditableTextStyle?: Record<string, unknown>;

    /**
     * The ID of the preset selected for the lead capture form.
     * @example 5d362a29-f43e-42fb-be34-16449f9efc07
     */

    presetId?: null | string;

    /**
     * The style of the form's primary button.
     */

    primaryButtonStyle?: Record<string, unknown>;

    /**
     * The URL of the privacy policy related to the form.
     * @example https://mywebsite.com/privacy-policy
     */

    privacyPolicyUrl?: null | string;

    /**
     * Whether the original event registration settings carried over to the On Demand video.
     */

    registrationSettingsCarried?: boolean;

    /**
     * The style of the form's secondary button.
     */

    secondaryButtonStyle?: Record<string, unknown>;
  };
};

/** Result type for PATCH /lead_capture/{resource_type}/{resource_id}/form */
export type PatchLeadCaptureResourceIdFormResult<Paths extends readonly string[]> = Select<
  ResourceLeadCaptureForm,
  Paths
>;

/**
 * Update a lead capture form for a resource
 * This method updates the lead capture form for the resource.
 */
export async function patchLeadCaptureResourceIdForm<
  Paths extends PatchLeadCaptureResourceIdFormFields,
>({
  baseUrl,
  select,
  variables,
  where: { resourceType, resourceId },
  ...options
}: PatchLeadCaptureResourceIdFormOptions<Paths>): Promise<
  PatchLeadCaptureResourceIdFormResult<Paths>
> {
  return measureLatency('patchLeadCaptureResourceIdForm', 'PATCH', async () => {
    let res = await fetch(
      `${baseUrl}/lead_capture/${resourceType}/${resourceId}/form?fields=${select
        .map(intoSnakeCase)
        .join(',')}`,
      {
        ...options,
        method: 'PATCH',
        body: JSON.stringify(deepSnakeCase(variables)),
      },
    );

    if (!res.ok) {
      throw new NetworkError('A network error occurred', res.status, res);
    }

    if (res.status === 204) {
      return null as unknown as PatchLeadCaptureResourceIdFormResult<Paths>;
    }

    if (!res.headers.get('content-type')?.match(/^application\/(.+)?json$/)) {
      throw new Error('Expected JSON response');
    }

    let json = await res.json();
    return deepCamelCase(json);
  });
}
